import { FC } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import { ComboboxMulti, FreeInputCombobox, ComboboxMultiAsync } from "@/components/elements/Combobox";
import { partnerSearchConfig } from "@/api/services/main/partner";
import { InputDatePicker } from "@/components/elements/InputDatePicker/InputDatePicker";
import { Input } from "@/components/elements/Input";
import { INPUT_BOX_HEIGHT } from "@/constants/ui";
import { TCaseMatchFilterFormExtended } from "@/features/case/components/CaseMatches/case-match-filter-shared";
import { useCaseMatchFilterMasterData } from "@/features/case/components/CaseMatches/use-case-match-filter-master-data";
import { TOption } from "@/types/common";
import { dayjs } from "@/utils/dayjs";
import { Dayjs } from "dayjs";

type CaseMatchFilterFormFieldsProps = {
  filterValues: TCaseMatchFilterFormExtended;
  onFilterChange: (field: keyof TCaseMatchFilterFormExtended, value: unknown) => void;
  /** Stable options from unfiltered list — not affected by active filters */
  talentNumberOptions?: TOption[];
  fullNameOptions?: TOption[];
  nickNameOptions?: TOption[];
};

/**
 * Filter form fields for case match talent filtering.
 * Layout: 2-column grid matching the design with 12 fields.
 */
export const CaseMatchFilterFormFields: FC<CaseMatchFilterFormFieldsProps> = ({
  filterValues,
  onFilterChange,
  talentNumberOptions,
  fullNameOptions,
  nickNameOptions,
}) => {
  const { truncatedOptions } = useCaseMatchFilterMasterData();

  return (
    <Grid container rowSpacing={2} columnSpacing={3}>
      {/* Row 1: 人材管理番号 / 名前 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">人材管理番号</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.talentNumber}
            onChange={(value) => onFilterChange("talentNumber", value)}
            options={talentNumberOptions ?? truncatedOptions.talentNumber}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">名前</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.fullName}
            onChange={(value) => onFilterChange("fullName", value)}
            options={fullNameOptions ?? truncatedOptions.fullName}
          />
        </Stack>
      </Grid>

      {/* Row 2: 呼び名 / 他提案 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">呼び名</Typography>
          <FreeInputCombobox
            placeholder="入力してください"
            value={filterValues.nickName}
            onChange={(value) => onFilterChange("nickName", value)}
            options={nickNameOptions ?? truncatedOptions.nickName}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">他提案</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.hasOtherProposal}
            onChange={(value) => onFilterChange("hasOtherProposal", value)}
            options={[
              { label: "あり", value: "1" },
              { label: "なし", value: "0" },
            ]}
          />
        </Stack>
      </Grid>

      {/* Row 3: 国籍 / 性別 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">国籍</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.nationalityId}
            onChange={(value) => onFilterChange("nationalityId", value)}
            options={truncatedOptions.nationality}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">性別</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.gender}
            onChange={(value) => onFilterChange("gender", value)}
            options={truncatedOptions.gender}
          />
        </Stack>
      </Grid>

      {/* Row 4: 年齢 / 在留資格 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">年齢</Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Input
              nonControl
              value={filterValues.ageFrom}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                onFilterChange("ageFrom", v);
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
            <Typography variant="cap12Light">〜</Typography>
            <Input
              nonControl
              value={filterValues.ageTo}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9]/g, "");
                onFilterChange("ageTo", v);
              }}
              sx={{ flex: 1, minWidth: 0 }}
            />
          </Stack>
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">在留資格</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.residenceStatusId}
            onChange={(value) => onFilterChange("residenceStatusId", value)}
            options={truncatedOptions.residenceStatus}
          />
        </Stack>
      </Grid>

      {/* 在留期限（開始〜終了）*/}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">在留期限</Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
              <InputDatePicker
                nonControl
                value={filterValues.periodOfStayFrom ? dayjs(filterValues.periodOfStayFrom) : null}
                onChange={(value: Dayjs | null) => onFilterChange("periodOfStayFrom", value ? value.format("YYYY-MM-DD") : "")}
                label="在留期限"
                maxDate={filterValues.periodOfStayTo ? dayjs(filterValues.periodOfStayTo) : undefined}
              />
            </Box>
            <Typography variant="cap12Light" sx={{ height: INPUT_BOX_HEIGHT, display: "flex", alignItems: "center" }}>
              〜
            </Typography>
            <Box sx={{ flex: 1 }}>
              <InputDatePicker
                nonControl
                value={filterValues.periodOfStayTo ? dayjs(filterValues.periodOfStayTo) : null}
                onChange={(value: Dayjs | null) => onFilterChange("periodOfStayTo", value ? value.format("YYYY-MM-DD") : "")}
                label="在留期限"
                minDate={filterValues.periodOfStayFrom ? dayjs(filterValues.periodOfStayFrom) : undefined}
              />
            </Box>
          </Stack>
        </Stack>
      </Grid>

      {/* 日本語能力 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">日本語能力</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.languageTestLevelId}
            onChange={(value) => onFilterChange("languageTestLevelId", value)}
            options={truncatedOptions.japaneseLevelType}
          />
        </Stack>
      </Grid>

      {/* Row 6: パートナー / 担当者 */}
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">パートナー</Typography>
          <ComboboxMultiAsync
            placeholder="選択してください"
            value={filterValues.partnerId}
            onChange={(value) => onFilterChange("partnerId", value)}
            searchConfig={partnerSearchConfig}
          />
        </Stack>
      </Grid>
      <Grid item xs={6}>
        <Stack spacing={0.5}>
          <Typography variant="cap12Light">担当者</Typography>
          <ComboboxMulti
            placeholder="選択してください"
            value={filterValues.personInChargeId}
            onChange={(value) => onFilterChange("personInChargeId", value)}
            options={truncatedOptions.personInCharge}
          />
        </Stack>
      </Grid>
    </Grid>
  );
};
